import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ShoppingCart, Truck } from 'lucide-react';
import { PO_STATUS, dateLabel } from '@adisys/shared';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { MetaBadge, Section, qtyLabel, type TabProps } from '../../../lib/crm';
import { EmptyState, Skeleton } from '../../../components/ui';
import { OrderAllocation } from '../Procurement';

export function ProcurementTab({ project, reload }: TabProps) {
  const { can } = useAuth();
  const order = project.summary?.order;
  const seesProcurement = can('crm.procurement.view') || can('crm.finance.view');
  const pos = useQuery({
    queryKey: ['crm', 'procurement', 'pos', { project: project.projectCode }],
    enabled: !!order && can('crm.procurement.view'),
    queryFn: () => api.get('/crm/procurement/purchase-orders', { q: project.projectCode, size: 50 }),
  });

  if (!order) {
    return <EmptyState icon={<Truck className="h-5 w-5" />} title="Procurement has not started"
      description="Stock is reserved and purchases are raised automatically as soon as the customer approves a quotation." />;
  }
  const units = Number(order.units), secured = Number(order.secured), delivered = Number(order.delivered);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-card p-4 ring-1 ring-line">
        <p className="text-sm text-ink-700">
          <strong className="text-ink-900">{qtyLabel(secured)}</strong> of {qtyLabel(units)} units are secured (in stock for this order or delivered);{' '}
          <strong className="text-ink-900">{qtyLabel(delivered)}</strong> delivered.
          {secured < units ? ' Procurement is still partial.' : delivered < units ? ' Everything is in hand; waiting on dispatch.' : ' All delivered.'}
        </p>
        {can('crm.procurement.view') && (
          <Link className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline" to="/crm/procurement">
            Open procurement desk <ArrowRight className="h-4 w-4" /></Link>)}
      </div>

      {seesProcurement ? (
        <Section title="Allocation by line" description="How each item of the approved order is covered.">
          <OrderAllocation orderId={order.id} manage={can('crm.procurement.manage')} onChanged={reload} />
        </Section>
      ) : <p className="text-sm text-ink-500">Line-level stock detail is visible to the procurement and finance teams.</p>}

      {can('crm.procurement.view') && (
        <Section title="Purchase orders for this project">
          {pos.isLoading ? <Skeleton className="h-16 w-full" /> : (pos.data?.data ?? []).length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-ink-500"><ShoppingCart className="h-4 w-4" />No purchase order placed. Everything may have come from stock.</p>
          ) : (
            <ul className="divide-y divide-line rounded-card bg-card ring-1 ring-line">
              {pos.data.data.map((p: any) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                  <div><Link className="font-medium text-ink-900 hover:underline" to={`/crm/procurement/purchase-orders/${p.id}`}>{p.poNo}</Link>
                    <p className="text-xs text-ink-500">{p.vendorName} · received {qtyLabel(p.unitsReceived)} of {qtyLabel(p.unitsOrdered)}
                      {p.expectedDelivery ? ` · expected ${dateLabel(p.expectedDelivery)}` : ''}</p></div>
                  <MetaBadge meta={PO_STATUS} value={p.status} />
                </li>))}
            </ul>)}
        </Section>)}
    </div>
  );
}
